import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { X, Smartphone } from 'lucide-react';
import {
  isAndroid,
  isInstalled,
  promptPWAInstall,
  openGooglePlay,
} from '../../utils/pwaInstall';

const SNACKBAR_DISMISS_KEY = 'pista_app_install_snackbar_dismissed';

/**
 * Subtle snackbar shown to logged-in Android users.
 * Triggers the PWA install prompt when available, otherwise opens Google Play.
 * Hides if a related app/PWA is already installed (via getInstalledRelatedApps),
 * and persists a manual dismiss in localStorage.
 */
function AppInstallSnackbar({ user }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    if (Capacitor.isNativePlatform()) return undefined;
    if (!isAndroid() || isInstalled()) return undefined;
    if (localStorage.getItem(SNACKBAR_DISMISS_KEY)) return undefined;

    let cancelled = false;
    let timer = null;

    const show = () => {
      if (!cancelled) setVisible(true);
    };

    if (typeof navigator !== 'undefined' && 'getInstalledRelatedApps' in navigator) {
      navigator.getInstalledRelatedApps()
        .then(related => {
          if (cancelled) return;
          if (related.length > 0) {
            // Pista is already installed; remember not to prompt again
            localStorage.setItem(SNACKBAR_DISMISS_KEY, 'true');
            return;
          }
          timer = setTimeout(show, 1200);
        })
        .catch(() => {
          if (!cancelled) timer = setTimeout(show, 1200);
        });
    } else {
      timer = setTimeout(show, 1200);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem(SNACKBAR_DISMISS_KEY, 'true');
    setVisible(false);
  };

  const handleInstall = async () => {
    const result = await promptPWAInstall();
    if (result === 'unavailable') {
      openGooglePlay();
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="app-install-snackbar"
      style={{
        position: 'fixed',
        /* Sit above the mobile bottom nav (65px) + safe area */
        bottom: 'calc(var(--mobile-navigation-height, 65px) + env(safe-area-inset-bottom, 0px) + 0.75rem)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.6rem 0.75rem',
        borderRadius: '9999px',
        backgroundColor: 'var(--pista-green-primary, #2E7D32)',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        fontSize: '0.85rem',
        maxWidth: 'calc(100% - 1.5rem)',
      }}
    >
      <Smartphone size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Get the app
      </span>
      <button
        type="button"
        onClick={handleInstall}
        style={{
          padding: '0.35rem 0.75rem',
          borderRadius: '9999px',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Install
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          background: 'rgba(255, 255, 255, 0.15)',
          color: '#ffffff',
          cursor: 'pointer',
          borderRadius: '50%',
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default AppInstallSnackbar;
